/**
 * V2 main / landing — banner hero, org logo, banner title + description, Create account / Sign in, Continue as guest.
 * No menu/order/plans tiles; no "Powered by Merak" footer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
} from 'react-native';
import { CommonActions } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AppLinearGradient as LinearGradient } from '../components/ui/AppLinearGradient';
import { AnimatedSection } from '../components/common/AnimatedSection';
import { Clickable } from '../components/common/Clickable';
import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { OrganizationLogo } from '../components/common/OrganizationLogo';
import { ErrorState } from '../components/common/ErrorState';
import { SignInCredentialForm, SignUpCredentialForm } from '../auth/variants/banner_guest';
import { DevHubGuestDevToolsEntry } from '../exDev';
import { useOnboardingData } from './hooks/useOnboardingData';
import { useBanners, useConsultationsFeatureEnabled, fetchInitialConfig, getConfigLoadError } from '../data/domains/config';
import { useAuthNavigation, useRedirectAuthenticatedFromGuestWelcome } from '../shared/navigation/useAuthNavigation';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { enrichSignupOtpParams } from '../shared/auth';
import { showSuccessAlert } from '../components/appAlerts';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import { useTranslation, getApiBannerTitle, getApiBannerDescription, getLocale, layoutLtrStyle } from '../shared/i18n';
import {
  androidBannerGuestCtaMinHeight,
  androidBannerGuestFooterPaddingBottom,
  androidBannerGuestHeroColumnLayoutStyle,
  androidBannerGuestLangPillStyle,
  androidBannerGuestTopBarPaddingTop,
  isBannerGuestMainScreenAndroid,
} from './mainScreenBannerGuestAndroidParity';

type BottomSheetType = null | 'signin' | 'signup';

type ThemeAppearance = 'light' | 'dark';

interface BannerGuestLangPillChrome {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

/** Readable lang chip on hero banners and plain backgrounds in light/dark mode. */
function resolveBannerGuestLangPillChrome(
  colors: ThemeColors,
  appearance: ThemeAppearance,
  has_hero_image: boolean,
): BannerGuestLangPillChrome {
  const is_dark = appearance === 'dark';

  if (has_hero_image) {
    return {
      backgroundColor: hexToRgba(colors.black, is_dark ? 0.72 : 0.62) ?? 'rgba(0,0,0,0.65)',
      borderColor: hexToRgba(colors.white, is_dark ? 0.42 : 0.5) ?? colors.white,
      textColor: colors.white,
    };
  }

  return {
    backgroundColor: colors.surface,
    borderColor: is_dark ? colors.borderSubtle ?? colors.border : colors.border,
    textColor: colors.text,
  };
}

function createMainScreenBannerGuestStyles(c: ThemeColors, has_hero_image: boolean) {
  const title_color = has_hero_image ? c.white : c.text;
  const body_secondary = has_hero_image ? hexToRgba(c.white, 0.92) : c.textSecondary;
  const guest_color = has_hero_image ? c.white : c.primary;
  const shadow = has_hero_image
    ? {
        textShadowColor: hexToRgba(c.black, 0.35),
        textShadowOffset: { width: 0, height: 1 } as const,
        textShadowRadius: 6,
      }
    : {};

  return StyleSheet.create({
    screenRootContainer: {
      flex: 1,
      backgroundColor: c.background,
    },
    backgroundBannerLtrContainer: {
      ...layoutLtrStyle,
    },
    heroScrim: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
    },
    topLanguageBar: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 2,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.lg,
    },
    langPillButton: {
      alignSelf: 'center',
      ...Platform.select({
        ios: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: has_hero_image ? 0.28 : 0.12,
          shadowRadius: 6,
        },
        android: {
          elevation: has_hero_image ? 4 : 2,
        },
        default: {},
      }),
    },
    langPillText: {
      ...appTheme.typography.headerTitle,
      fontWeight: '600',
    },
    animatedContentSection: {
      flex: 1,
    },
    footerColumn: {
      flex: 1,
      justifyContent: 'flex-end',
    },
    footerCopyBlock: {
      alignSelf: 'stretch',
      alignItems: 'flex-start',
    },
    footerCopyBlockSpacing: {
      gap: appTheme.spacing.sm,
    },
    heroCopyLtrShell: {
      alignSelf: 'stretch',
      width: '100%',
      ...layoutLtrStyle,
    },
    organizationNameText: {
      ...appTheme.typography.headline,
      color: title_color,
      fontWeight: '700',
      ...shadow,
    },
    organizationNameTextSpacing: {
      marginBottom: appTheme.spacing.sm,
    },
    bannerHeroGapAfterLogo: {
      marginTop: appTheme.spacing.sm,
    },
    bannerHeroCopyInset: {
      paddingHorizontal: appTheme.spacing.md,
    },
    organizationDescriptionText: {
      ...appTheme.typography.body,
      color: body_secondary,
      ...(has_hero_image
        ? {
            textShadowColor: hexToRgba(c.black, 0.3),
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 4,
          }
        : {}),
    },
    footerCopyMaxWidth: {
      width: '100%',
      maxWidth: 420,
      alignSelf: 'center',
    },
    ctaButtonsRow: {
      flexDirection: 'row',
      gap: appTheme.spacing.md,
      alignSelf: 'stretch',
      alignItems: 'stretch',
      ...layoutLtrStyle,
    },
    ctaButtonsRowSpacing: {
      gap: appTheme.spacing.lg,
      marginTop: appTheme.spacing.xl,
    },
    /** Sign in — narrower slot (flex 2), always left under logo (LTR row). */
    ctaCellSecondary: {
      flex: 2,
      minWidth: 0,
    },
    /** Create account — wider slot (flex 3). */
    ctaCellPrimary: {
      flex: 3,
      minWidth: 0,
    },
    ctaPressable: {
      width: '100%',
      minHeight: 50,
      borderRadius: 15,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.md,
    },
    ctaPressableFilled: {
      backgroundColor: c.primary,
    },
    ctaPressableOutline: {
      borderWidth: 1,
      borderColor: has_hero_image ? hexToRgba(c.white, 0.48) : c.border,
      backgroundColor: has_hero_image
        ? Platform.OS === 'android'
          ? 'rgba(255,255,255,0.22)'
          : 'rgba(255,255,255,0.12)'
        : c.surface,
    },
    ctaPressablePressed: {
      opacity: 0.88,
    },
    ctaLabel: {
      ...appTheme.typography.body,
      fontWeight: '700',
      textAlign: 'center',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    ctaLabelFilled: {
      color: c.onPrimary,
    },
    ctaLabelOutline: {
      color: has_hero_image ? c.white : c.primary,
    },
    guestLinkWrap: {
      alignSelf: 'center',
      marginTop: appTheme.spacing.lg,
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
    },
    guestLinkText: {
      ...appTheme.typography.bodySmall,
      fontWeight: '600',
      color: guest_color,
      textDecorationLine: 'underline',
      ...(has_hero_image
        ? {
            textShadowColor: hexToRgba(c.black, 0.35),
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 2,
          }
        : {}),
    },
    bottomSheetKeyboardAvoidingContainer: {
      minHeight: 380,
      maxHeight: '100%',
    },
    bottomSheetScrollView: {
      flexGrow: 1,
    },
    bottomSheetScrollViewContent: {
      flexGrow: 1,
      paddingHorizontal: appTheme.spacing.xl,
      paddingTop: 0,
    },
    bottomSheetPanel: {
      overflow: 'hidden',
    },
  });
}

const MainScreenBannerGuest = () => {
  const { t, locale, setLocale } = useTranslation();
  const insets = useSafeAreaInsets();
  const navigation = useAuthNavigation();
  const { state: auth_sheet_state, openReset, openOtp } = useAuthSheets();
  useRedirectAuthenticatedFromGuestWelcome();
  const [config_error, set_config_error] = useState<string | null>(() => getConfigLoadError());
  const { logo_uri } = useOnboardingData();
  const show_consultation_entry = useConsultationsFeatureEnabled();
  const { backgroundImageUri, heroBanner, mainHeroBannerCopySuppressed } = useBanners('banner');
  const [visible_bottom_sheet, set_visible_bottom_sheet] = useState<BottomSheetType>(null);
  const is_android = isBannerGuestMainScreenAndroid();
  const footer_bottom_padding = is_android ? androidBannerGuestFooterPaddingBottom(insets.bottom) : insets.bottom + appTheme.spacing.lg;
  const android_hero_column_style = is_android ? androidBannerGuestHeroColumnLayoutStyle() : null;
  const android_cta_min_height = is_android ? androidBannerGuestCtaMinHeight() : undefined;
  const colors = useThemeColors() ?? appTheme.colors;
  const appearance = useThemeStore(s => s.appearance);
  const has_hero_image = Boolean(backgroundImageUri);
  const lang_pill_chrome = useMemo(
    () => resolveBannerGuestLangPillChrome(colors, appearance, has_hero_image),
    [colors, appearance, has_hero_image],
  );
  const styles = useMemo(() => createMainScreenBannerGuestStyles(colors, has_hero_image), [colors, has_hero_image]);

  const banner_hero_title = useMemo(() => {
    if (mainHeroBannerCopySuppressed || !heroBanner) {
      return '';
    }
    return getApiBannerTitle(heroBanner as Record<string, unknown>).trim();
  }, [mainHeroBannerCopySuppressed, heroBanner, locale]);

  const banner_hero_description = useMemo(() => {
    if (mainHeroBannerCopySuppressed || !heroBanner) {
      return '';
    }
    return getApiBannerDescription(heroBanner as Record<string, unknown>).trim();
  }, [mainHeroBannerCopySuppressed, heroBanner, locale]);

  const hero_scrim_gradient = useMemo(
    () => [hexToRgba(colors.black, 0), hexToRgba(colors.black, 0.42), hexToRgba(colors.black, 0.62)] as const,
    [colors.black],
  );

  const outline_border_color = has_hero_image ? hexToRgba(colors.white, 0.48) : colors.border;
  const outline_surface_color = has_hero_image
    ? Platform.OS === 'android'
      ? 'rgba(255,255,255,0.22)'
      : 'rgba(255,255,255,0.12)'
    : colors.surface;

  useEffect(() => {
    set_config_error(getConfigLoadError());
  }, []);

  const handle_retry_config = useCallback(async () => {
    set_config_error(null);
    await fetchInitialConfig();
    set_config_error(getConfigLoadError());
  }, []);

  const close_bottom_sheet = useCallback(() => set_visible_bottom_sheet(null), []);
  const open_sign_in_sheet = useCallback(() => set_visible_bottom_sheet('signin'), []);
  const open_sign_up_sheet = useCallback(() => set_visible_bottom_sheet('signup'), []);

  const handle_forgot_password = useCallback(
    (phone_no: string, country_iso_code: string) => {
      close_bottom_sheet();
      showSuccessAlert(t, { message: t('auth.resetCodeSent') });
      openReset({
        phoneNo: phone_no,
        country_iso_code,
        redirectTo: 'Main',
        historyRoute: 'Main',
      });
    },
    [close_bottom_sheet, openReset, t],
  );

  const handle_register_success = useCallback(
    (params: {
      phone: string;
      countryCode: string;
      context: string;
      redirectTo: string;
      signupPassword: string;
      customer_id: number | undefined;
    }) => {
      close_bottom_sheet();
      openOtp(
        enrichSignupOtpParams({
          phone: params.phone,
          countryCode: params.countryCode,
          context: params.context,
          redirectTo: params.redirectTo === 'MainTabs' ? 'SubscriptionList' : params.redirectTo,
          signupPassword: params.signupPassword,
        }),
      );
    },
    [close_bottom_sheet, openOtp],
  );

  const sheet_header_height = 56;
  const wrap_sheet_content = useCallback(
    (children: React.ReactNode) => (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.bottomSheetKeyboardAvoidingContainer}
        keyboardVerticalOffset={Platform.OS === 'ios' ? insets.top + sheet_header_height : 0}
        pointerEvents="box-none">
        <ScrollView
          style={styles.bottomSheetScrollView}
          contentContainerStyle={[styles.bottomSheetScrollViewContent, { paddingBottom: insets.bottom + 32 }]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          nestedScrollEnabled>
          {children}
        </ScrollView>
      </KeyboardAvoidingView>
    ),
    [insets.bottom, insets.top, styles],
  );

  const language_pill_label = getLocale() === 'ar' ? 'English' : 'العربية';
  const toggle_language = useCallback(() => setLocale(getLocale() === 'ar' ? 'en' : 'ar'), [setLocale]);

  const continue_as_guest = useCallback(() => {
    navigation.dispatch(
      CommonActions.navigate({
        name: 'SubscriptionList',
        params: { guestExplore: true },
      }),
    );
  }, [navigation]);

  /** Close local auth sheet when a global auth flow takes over (OTP, reset, etc.) — matches v2 `closeBottomSheet` before handoff. */
  useEffect(() => {
    if (auth_sheet_state != null && visible_bottom_sheet != null) {
      set_visible_bottom_sheet(null);
    }
  }, [auth_sheet_state, visible_bottom_sheet]);

  if (config_error) {
    return (
      <View style={styles.screenRootContainer}>
        <ErrorState
          title={t('common.error')}
          message={config_error}
          onRetry={handle_retry_config}
          issue_context={{ page: 'Main Screen', issue: 'Initial configuration failed to load', kind: 'api', api_endpoint: 'initial', http_method: 'GET' }}
        />
      </View>
    );
  }

  return (
    <View key={locale} style={styles.screenRootContainer}>
      {has_hero_image ? (
        <>
          <View style={[StyleSheet.absoluteFill, styles.backgroundBannerLtrContainer, { zIndex: 0 }]} pointerEvents="none">
            <Image source={{ uri: backgroundImageUri! }} style={StyleSheet.absoluteFill} resizeMode="cover" />
          </View>
          <LinearGradient pointerEvents="none" colors={hero_scrim_gradient} locations={[0, 0.55, 1]} style={styles.heroScrim} />
        </>
      ) : null}

      <AnimatedSection
        index={0}
        variant="fadeDown"
        style={[styles.animatedContentSection, android_hero_column_style, { zIndex: 1, backgroundColor: 'transparent' }]}>
        <View
          style={[
            styles.footerColumn,
            { paddingBottom: footer_bottom_padding / 1.5, paddingHorizontal: appTheme.spacing.xl },
            !has_hero_image && { backgroundColor: colors.background },
          ]}>
          <View style={[styles.footerCopyBlock, styles.footerCopyBlockSpacing, styles.footerCopyMaxWidth]}>
            <View style={styles.heroCopyLtrShell}>
              <OrganizationLogo logoUri={logo_uri ?? null} />
              {banner_hero_title ? (
                <Text
                  style={[
                    styles.bannerHeroGapAfterLogo,
                    styles.organizationNameText,
                    styles.organizationNameTextSpacing,
                    styles.bannerHeroCopyInset,
                  ]}>
                  {banner_hero_title}
                </Text>
              ) : null}
              {banner_hero_description ? (
                <Text
                  style={[
                    !banner_hero_title ? styles.bannerHeroGapAfterLogo : undefined,
                    styles.organizationDescriptionText,
                    styles.bannerHeroCopyInset,
                  ]}
                  numberOfLines={4}>
                  {banner_hero_description}
                </Text>
              ) : null}
            </View>
          </View>

          <View style={[styles.ctaButtonsRow, styles.ctaButtonsRowSpacing]}>
            <Pressable
              onPress={open_sign_in_sheet}
              accessibilityRole="button"
              accessibilityLabel={t('auth.signIn')}
              style={({ pressed }) => [
                styles.ctaCellSecondary,
                styles.ctaPressable,
                styles.ctaPressableOutline,
                android_cta_min_height != null && { minHeight: android_cta_min_height },
                pressed && styles.ctaPressablePressed,
              ]}>
              <Text style={[styles.ctaLabel, styles.ctaLabelOutline]} numberOfLines={1}>
                {t('auth.signIn')}
              </Text>
            </Pressable>
            <Pressable
              onPress={open_sign_up_sheet}
              accessibilityRole="button"
              accessibilityLabel={t('auth.createAccount')}
              style={({ pressed }) => [
                styles.ctaCellPrimary,
                styles.ctaPressable,
                styles.ctaPressableFilled,
                android_cta_min_height != null && { minHeight: android_cta_min_height },
                pressed && styles.ctaPressablePressed,
              ]}>
              <Text style={[styles.ctaLabel, styles.ctaLabelFilled]} numberOfLines={1}>
                {t('auth.createAccount')}
              </Text>
            </Pressable>
          </View>

          {show_consultation_entry ? (
            <Clickable
              onPress={() => navigation.navigate('Consultation')}
              haptics
              style={styles.guestLinkWrap}
              accessibilityRole="button"
              accessibilityLabel={t('account.consultation')}>
              <Text style={styles.guestLinkText}>{t('account.consultation')}</Text>
            </Clickable>
          ) : null}

          <Clickable
            onPress={continue_as_guest}
            haptics
            style={styles.guestLinkWrap}
            accessibilityRole="button"
            accessibilityLabel={t('auth.continueAsGuest')}>
            <Text style={styles.guestLinkText}>{t('auth.continueAsGuest')}</Text>
          </Clickable>
        </View>
      </AnimatedSection>

      <View
        style={[
          styles.topLanguageBar,
          {
            paddingTop: is_android ? androidBannerGuestTopBarPaddingTop(insets.top) : insets.top + appTheme.spacing.sm,
          },
        ]}
        pointerEvents="box-none">
        <Clickable
          onPress={toggle_language}
          haptics
          accessibilityLabel={t('settings.appLanguage')}
          style={[
            styles.langPillButton,
            is_android && androidBannerGuestLangPillStyle(),
            {
              minHeight: 36,
              minWidth: 100,
              borderRadius: 15,
              borderWidth: 1,
              borderColor: lang_pill_chrome.borderColor,
              backgroundColor: lang_pill_chrome.backgroundColor,
              alignItems: 'center',
              justifyContent: 'center',
              paddingHorizontal: appTheme.spacing.md,
            },
          ]}>
          <Text style={[styles.langPillText, { color: lang_pill_chrome.textColor }]} numberOfLines={1}>
            {language_pill_label}
          </Text>
        </Clickable>
        <DevHubGuestDevToolsEntry />
      </View>

      <BottomSheetPopup
        visible={visible_bottom_sheet === 'signin'}
        onClose={close_bottom_sheet}
        title={t('auth.signIn')}
        animationType="slide"
        sheetTopBorderRadius={appTheme.borderRadius.xl}
        sheetStyle={styles.bottomSheetPanel}>
        {wrap_sheet_content(
          <SignInCredentialForm onClose={close_bottom_sheet} onOpenSignUp={open_sign_up_sheet} onForgotPassword={handle_forgot_password} />,
        )}
      </BottomSheetPopup>

      <BottomSheetPopup
        visible={visible_bottom_sheet === 'signup'}
        onClose={close_bottom_sheet}
        title={t('auth.signUp')}
        animationType="slide"
        sheetTopBorderRadius={appTheme.borderRadius.xl}
        sheetStyle={styles.bottomSheetPanel}>
        {wrap_sheet_content(
          <SignUpCredentialForm onClose={close_bottom_sheet} onRegisterSuccess={handle_register_success} onOpenSignIn={open_sign_in_sheet} />,
        )}
      </BottomSheetPopup>
    </View>
  );
};

export default MainScreenBannerGuest;
